/**
 * Field validation utility based on label names
 * Returns validation regex and transformation rules for specific field types
 * All error messages are written in clear, user-understandable language
 */

/**
 * Get validation rules for a field based on its label
 * @param {string} label - The field label
 * @returns {Object} - Object containing validation regex, error message, and transformation rules
 */
export const getFieldValidation = (label) => {
  if (!label || typeof label !== 'string') {
    return null;
  }

  // Normalize label: convert to lowercase, trim, and replace underscores/hyphens with spaces
  const normalizedLabel = label.toLowerCase().trim().replace(/[_-]/g, ' ');

  // Phone Number or Mobile Number: 10 digits, No special characters
  if (normalizedLabel.includes('phone number') || normalizedLabel.includes('mobile number')) {
    return {
      regex: /^\d{10}$/,
      errorMessage: 'Phone Number must contain exactly 10 digits. Please enter only numbers without any spaces, dashes, or special characters.',
      transform: (value) => value.replace(/\D/g, ''), // Remove non-digits
      maxLength: 10
    };
  }

  // Aadhaar Number or Aadhaar No: Exactly 12 digits, should not start with 0 or 1
  if (normalizedLabel.includes('aadhaar number') || normalizedLabel.includes('aadhaar no')) {
    return {
      regex: /^[2-9]\d{11}$/,
      errorMessage: 'Aadhaar Number must contain exactly 12 digits and cannot start with 0 or 1. Please enter a valid 12-digit Aadhaar number.',
      transform: (value) => value.replace(/\D/g, ''), // Remove non-digits
      maxLength: 12
    };
  }

  // PAN Number or PAN No: 10 characters - 5 letters, 4 digits, 1 letter (uppercase)
  if (normalizedLabel.includes('pan number') || normalizedLabel.includes('pan no')) {
    return {
      regex: /^[A-Z]{5}\d{4}[A-Z]{1}$/,
      errorMessage: 'PAN Number must be 10 characters: 5 uppercase letters, followed by 4 digits, and ending with 1 uppercase letter. Example: ABCDE1234F',
      transform: (value) => value.replace(/[^A-Za-z0-9]/g, '').toUpperCase(), // Remove special chars and convert to uppercase
      maxLength: 10,
      autoUppercase: true
    };
  }

  // Voter ID: 10 characters - 3 uppercase letters followed by 7 digits
  if (normalizedLabel.includes('voter id')) {
    return {
      regex: /^[A-Z]{3}\d{7}$/,
      errorMessage: 'Voter ID must be 10 characters: 3 uppercase letters followed by 7 digits. Example: ABC1234567',
      transform: (value) => {
        // Keep first 3 as letters (uppercase), rest as digits
        const cleaned = value.replace(/[^A-Za-z0-9]/g, '');
        const letters = cleaned.match(/^[A-Za-z]{0,3}/)?.[0] || '';
        const digits = cleaned.replace(/^[A-Za-z]{0,3}/, '').replace(/\D/g, '').substring(0, 7);
        return (letters.toUpperCase() + digits).substring(0, 10);
      },
      maxLength: 10,
      autoUppercase: true
    };
  }

  // Passport No: Check this first (more specific) before generic "passport"
  if (normalizedLabel.includes('passport no')) {
    return {
      regex: /^[A-Z]{1,2}\d{6,7}$/,
      errorMessage: 'Passport Number must be 8-9 characters: 1-2 uppercase letters followed by 6-7 digits. Example: A1234567 or AB1234567',
      transform: (value) => {
        const cleaned = value.replace(/[^A-Za-z0-9]/g, '');
        const letters = cleaned.match(/^[A-Za-z]{0,2}/)?.[0] || '';
        const digits = cleaned.replace(/^[A-Za-z]{0,2}/, '').replace(/\D/g, '').substring(0, 7);
        return (letters.toUpperCase() + digits).substring(0, 9);
      },
      maxLength: 9,
      autoUppercase: true
    };
  }

  // Passport (Generic): 8-9 alphanumeric characters, usually starts with 1-2 letters followed by numbers
  if (normalizedLabel.includes('passport')) {
    return {
      regex: /^[A-Z]{1,2}\d{6,7}$/,
      errorMessage: 'Passport Number must be 8-9 characters: 1-2 uppercase letters followed by 6-7 digits. Example: A1234567 or AB1234567',
      transform: (value) => {
        const cleaned = value.replace(/[^A-Za-z0-9]/g, '');
        const letters = cleaned.match(/^[A-Za-z]{0,2}/)?.[0] || '';
        const digits = cleaned.replace(/^[A-Za-z]{0,2}/, '').replace(/\D/g, '').substring(0, 7);
        return (letters.toUpperCase() + digits).substring(0, 9);
      },
      maxLength: 9,
      autoUppercase: true
    };
  }

  // Driving Licence ID or Driving Licence Number: Check this first (more specific)
  if (normalizedLabel.includes('driving licence id') || normalizedLabel.includes('driving licence number') || 
      normalizedLabel.includes('driving license id') || normalizedLabel.includes('driving license number')) {
    return {
      regex: /^[A-Z]{2}\d{2}\s?\d{9,11}$/,
      errorMessage: 'Driving Licence must be in the format: State code (2 letters) + RTO code (2 digits) + Year (2-4 digits) + Unique number (7 digits). Example: KA01 20110012345 or KA0120110012345',
      transform: (value) => {
        const cleaned = value.replace(/\s/g, '').toUpperCase();
        if (cleaned.length > 4) {
          const stateCode = cleaned.substring(0, 2);
          const rtoCode = cleaned.substring(2, 4);
          const rest = cleaned.substring(4).replace(/\D/g, '');
          // Insert space after RTO code if not present and length allows
          if (rest.length > 2 && !value.includes(' ')) {
            return stateCode + rtoCode + ' ' + rest;
          }
          return stateCode + rtoCode + rest;
        }
        return cleaned;
      },
      maxLength: 16,
      autoUppercase: true,
      customValidation: (value) => {
        const cleaned = value.replace(/\s/g, '').toUpperCase();
        if (cleaned.length < 13 || cleaned.length > 15) return false;
        const stateCode = cleaned.substring(0, 2);
        return /^[A-Z]{2}$/.test(stateCode);
      }
    };
  }

  // Driving Licence (India): State code (2 letters) + RTO code (2 digits) + Year (2-4 digits) + Unique number (7 digits)
  if (normalizedLabel.includes('driving licence') || normalizedLabel.includes('driving license')) {
    return {
      regex: /^[A-Z]{2}\d{2}\s?\d{9,11}$/,
      errorMessage: 'Driving Licence must be in the format: State code (2 letters) + RTO code (2 digits) + Year (2-4 digits) + Unique number (7 digits). Example: KA01 20110012345 or KA0120110012345',
      transform: (value) => {
        // Remove spaces, convert to uppercase
        const cleaned = value.replace(/\s/g, '').toUpperCase();
        // Allow one optional space between sections
        if (cleaned.length > 4) {
          const stateCode = cleaned.substring(0, 2);
          const rtoCode = cleaned.substring(2, 4);
          const rest = cleaned.substring(4).replace(/\D/g, '');
          // Insert space after RTO code if not present and length allows
          if (rest.length > 2 && !value.includes(' ')) {
            return stateCode + rtoCode + ' ' + rest;
          }
          return stateCode + rtoCode + rest;
        }
        return cleaned;
      },
      maxLength: 16, // KA01 + space + 2011 + 0012345 = 16 chars
      autoUppercase: true,
      customValidation: (value) => {
        const cleaned = value.replace(/\s/g, '').toUpperCase();
        if (cleaned.length < 13 || cleaned.length > 15) return false;
        const stateCode = cleaned.substring(0, 2);
        // Check if state code is valid (basic check)
        return /^[A-Z]{2}$/.test(stateCode);
      }
    };
  }

  // Ration Card ID: Check this first (more specific)
  if (normalizedLabel.includes('ration card id')) {
    return {
      regex: /^[A-Z0-9]{10,15}$/,
      errorMessage: 'Ration Card must be 10-15 alphanumeric characters (uppercase letters and numbers only). It may include an optional 2-3 letter prefix for state or district code. Example: KA012345678901 or TN09C1234567',
      transform: (value) => {
        const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        return cleaned.substring(0, 15); // Ensure max 15 characters
      },
      maxLength: 15,
      autoUppercase: true,
      customValidation: (value) => {
        const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        // Total length must be 10-15 characters
        if (cleaned.length < 10 || cleaned.length > 15) return false;
        return true;
      }
    };
  }

  // Ration Card (Generic): 10-15 alphanumeric characters, optional 2-3 letter prefix
  if (normalizedLabel.includes('ration card')) {
    return {
      regex: /^[A-Z0-9]{10,15}$/,
      errorMessage: 'Ration Card must be 10-15 alphanumeric characters (uppercase letters and numbers only). It may include an optional 2-3 letter prefix for state or district code. Example: KA012345678901 or TN09C1234567',
      transform: (value) => {
        const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        return cleaned.substring(0, 15); // Ensure max 15 characters
      },
      maxLength: 15,
      autoUppercase: true,
      customValidation: (value) => {
        const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        // Total length must be 10-15 characters
        if (cleaned.length < 10 || cleaned.length > 15) return false;
        return true;
      }
    };
  }

  // Workplace ID: 5-20 characters, letters, digits, underscore, hyphen
  if (normalizedLabel.includes('workplace id')) {
    return {
      regex: /^[A-Za-z0-9_-]{5,20}$/,
      errorMessage: 'Workplace ID must be 5-20 characters long. Only letters, numbers, underscores (_), and hyphens (-) are allowed. Example: EMP_1023 or STAFF-001',
      transform: (value) => value.replace(/[^A-Za-z0-9_-]/g, ''), // Remove invalid characters
      maxLength: 20,
      minLength: 5
    };
  }

  // LPG Consumer Number or LPG Consumer No: 11-17 digits, numeric only
  if (normalizedLabel.includes('lpg consumer number') || normalizedLabel.includes('lpg consumer no')) {
    return {
      regex: /^\d{11,17}$/,
      errorMessage: 'LPG Consumer Number must be 11-17 digits long. Please enter only numbers without any spaces or special characters. Example: 1234567890123',
      transform: (value) => value.replace(/\D/g, ''), // Remove non-digits
      maxLength: 17,
      minLength: 11
    };
  }

  // CID Crime No / Enquiry No: serial/CRM/division/department/year (e.g. 01/CRM/DFID/CID/2026)
  if (
    normalizedLabel.includes('cid crime') &&
    (normalizedLabel.includes('enquiry') || normalizedLabel.includes('crime no'))
  ) {
    return {
      regex: /^\d{1,6}\/CRM\/[A-Za-z0-9]+\/[A-Za-z0-9]+\/\d{4}$/i,
      errorMessage:
        'CID Crime No / Enquiry No must follow: 01/CRM/DFID/CID/2026 (serial/CRM/division/department/year). CRM is fixed; serial increases per division, department, and year.',
      transform: (value) => {
        const trimmed = String(value || '').trim();
        const match = trimmed.match(/^(\d{1,6})\/CRM\/([A-Za-z0-9]+)\/([A-Za-z0-9]+)\/(\d{4})$/i);
        if (!match) return trimmed;
        const serial = String(parseInt(match[1], 10)).padStart(2, '0');
        return `${serial}/CRM/${match[2].toUpperCase()}/${match[3].toUpperCase()}/${match[4]}`;
      },
      maxLength: 64,
    };
  }

  return null;
};

/**
 * Validate a field value based on its label
 * @param {string} label - The field label
 * @param {string} value - The field value
 * @returns {Object} - Object with isValid boolean and errorMessage string
 */
export const validateFieldByLabel = (label, value) => {
  const validation = getFieldValidation(label);
  
  if (!validation) {
    return { isValid: true, errorMessage: null };
  }

  if (!value || value.trim() === '') {
    return { isValid: true, errorMessage: null }; // Empty values are handled by required validation
  }

  const trimmedValue = value.trim();
  
  // If value becomes empty after transformation, it's invalid (e.g., "k" for phone number)
  if (validation.transform) {
    const transformed = validation.transform(trimmedValue);
    if (transformed === '' && trimmedValue !== '') {
      return { isValid: false, errorMessage: validation.errorMessage };
    }
  }
  
  // Check custom validation if exists
  if (validation.customValidation && !validation.customValidation(trimmedValue)) {
    return { isValid: false, errorMessage: validation.errorMessage };
  }

  // Check regex validation
  if (!validation.regex.test(trimmedValue)) {
    return { isValid: false, errorMessage: validation.errorMessage };
  }

  return { isValid: true, errorMessage: null };
};

/**
 * Transform field value based on its label (for auto-uppercase, formatting, etc.)
 * @param {string} label - The field label
 * @param {string} value - The field value
 * @returns {string} - Transformed value
 */
export const transformFieldValue = (label, value) => {
  const validation = getFieldValidation(label);
  
  if (!validation || !validation.transform) {
    return value;
  }

  return validation.transform(value);
};

